import { useState, useEffect, useRef } from "react";
import { ArrowLeft, Phone, MapPin, User, Clock, UserCheck, CheckCircle2, StickyNote, Loader2, Send, Crosshair, Video, VideoOff, Users, UserRound, Mail, Home, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, agentsApi, clientsApi } from "@/lib/api";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { timeAgo, formatDakar } from "@/lib/time";

const Row = ({ icon: Icon, label, value, accent }) => (
  <div className="flex items-start gap-3 py-2.5 border-b border-slate-800/80 last:border-0">
    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${accent || "text-slate-500"}`} />
    <div className="min-w-0">
      <p className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{label}</p>
      <p className="text-sm text-slate-200 break-words">{value}</p>
    </div>
  </div>
);

const AlertDetail = ({ alert, onBack, onUpdated, onHandled }) => {
  const [agent, setAgent] = useState("");
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [client, setClient] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState("");
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoState, setVideoState] = useState("idle"); // idle | loading | ready | error
  const videoUrlRef = useRef(null);

  useEffect(() => {
    setAgent(alert.assigned_to || "");
    setNote("");
  }, [alert.alert_id, alert.assigned_to]);

  useEffect(() => {
    agentsApi.list().then(setAgents).catch(() => setAgents([]));
    setClient(null);
    if (alert.user_phone) {
      clientsApi.byPhone(alert.user_phone).then((c) => {
        if (c) { setClient(c); }
        else {
          // Auto-register this citizen from the incoming alert (app-mobile sync)
          clientsApi.importFromApp({ name: alert.user_name, phone: alert.user_phone })
            .then((r) => setClient(r.client))
            .catch(() => setClient(null));
        }
      }).catch(() => setClient(null));
    }
  }, [alert.alert_id, alert.user_phone]);

  useEffect(() => {
    const revokePrev = () => {
      if (videoUrlRef.current) { URL.revokeObjectURL(videoUrlRef.current); videoUrlRef.current = null; }
    };
    revokePrev();
    setVideoUrl(null);
    if (alert.has_video && alert.video_path) {
      let cancelled = false;
      setVideoState("loading");
      api.getFileBlob(alert.video_path)
        .then((blob) => {
          if (cancelled) return;
          const url = URL.createObjectURL(blob);
          videoUrlRef.current = url;
          setVideoUrl(url);
          setVideoState("ready");
        })
        .catch(() => { if (!cancelled) setVideoState("error"); });
      return () => { cancelled = true; revokePrev(); };
    }
    setVideoState("idle");
    return revokePrev;
  }, [alert.alert_id, alert.has_video, alert.video_path]);

  const loc = alert.current_location;

  const doAssign = async () => {
    const chosen = agents.find((a) => a.id === selectedAgentId);
    if (!chosen) return toast.error("Sélectionnez un agent");
    const label = `${chosen.first_name} ${chosen.last_name} · ${chosen.zone}`;
    setBusy("assign");
    try {
      await api.assign(alert.alert_id, label);
      // Start real-time agent GPS tracking + send Twilio SMS to the agent
      let smsResult = null;
      if (loc) {
        const res = await agentsApi.assign(chosen.id, {
          alert_id: alert.alert_id,
          target_lat: loc.lat,
          target_lng: loc.lng,
          victim_name: alert.user_name,
          victim_phone: alert.user_phone,
        });
        smsResult = res?.sms;
      }
      toast.success(`Agent « ${chosen.first_name} ${chosen.last_name} » assigné`);
      if (smsResult?.sent) {
        toast.success(`SMS envoyé à l'agent (${smsResult.to})`);
      } else if (smsResult && !smsResult.sent) {
        toast.error(`SMS non envoyé : ${smsResult.error || "erreur inconnue"}`);
      }
      agentsApi.list().then(setAgents).catch(() => {});
      onUpdated();
    } catch {
      toast.error("Échec de l'assignation");
    } finally { setBusy(""); }
  };

  const doNote = async () => {
    if (!note.trim()) return;
    setBusy("note");
    try {
      await api.addNote(alert.alert_id, note.trim());
      toast.success("Note ajoutée");
      setNote("");
      onUpdated();
    } catch {
      toast.error("Échec de l'ajout de la note");
    } finally { setBusy(""); }
  };

  const doHandle = async () => {
    setBusy("handle");
    try {
      await api.endAlert(alert.alert_id, "resolved");
      toast.success("Alerte marquée comme traitée");
      onHandled(alert.alert_id);
    } catch {
      toast.error("Échec du traitement de l'alerte");
    } finally { setBusy(""); }
  };

  return (
    <div className="flex flex-col h-full" data-testid="alert-detail">
      <div className="p-4 border-b border-slate-800 flex items-center gap-3 shrink-0">
        <button data-testid="detail-back-btn" onClick={onBack} className="w-9 h-9 rounded-lg bg-slate-800 hover:bg-slate-700 flex items-center justify-center text-slate-300">
          <ArrowLeft className="w-4 h-4" />
        </button>
        <div className="min-w-0">
          <h3 className="font-display font-bold text-slate-100 truncate">{alert.user_name}</h3>
          <p className="text-xs font-mono text-red-400">Alerte SOS en cours</p>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        <div className="bg-slate-900/80 border border-slate-800 rounded-xl px-4">
          <Row icon={User} label="Personne en détresse" value={alert.user_name} accent="text-emerald-400" />
          <Row icon={Phone} label="Téléphone" value={<a href={`tel:${alert.user_phone}`} className="text-emerald-400 hover:underline" data-testid="detail-phone">{alert.user_phone}</a>} accent="text-emerald-400" />
          <Row icon={MapPin} label="Position GPS" value={loc ? `${loc.lat.toFixed(5)}, ${loc.lng.toFixed(5)}` : "—"} accent="text-yellow-400" />
          <Row icon={Crosshair} label="Précision" value={loc?.accuracy != null ? `± ${loc.accuracy} m` : "—"} />
          <Row icon={Clock} label="Déclenchée" value={`${formatDakar(alert.triggered_at)} (${timeAgo(alert.triggered_at)})`} />
          <Row icon={MapPin} label="Points du trajet" value={`${(alert.locations || []).length} position(s) enregistrée(s)`} accent="text-red-400" />
        </div>

        {/* Client record (auto-matched by phone) */}
        {client ? (
          <div data-testid="client-record" className="bg-emerald-950/20 border border-emerald-500/30 rounded-xl p-4 space-y-2.5">
            <p className="text-xs font-mono uppercase tracking-wider text-emerald-400 flex items-center gap-1.5"><UserRound className="w-3.5 h-3.5" /> Fiche client enregistrée</p>
            <p className="text-sm text-slate-100 font-semibold">{client.first_name} {client.last_name}</p>
            <div className="space-y-1.5 text-sm text-slate-300">
              {client.email && <div className="flex items-center gap-2"><Mail className="w-3.5 h-3.5 text-slate-500 shrink-0" /> {client.email}</div>}
              {client.address && <div className="flex items-center gap-2"><Home className="w-3.5 h-3.5 text-slate-500 shrink-0" /> {client.address}</div>}
              {(client.emergency_contact_name || client.emergency_contact_phone) && (
                <div className="flex items-start gap-2 text-amber-300 pt-1 border-t border-emerald-500/20 mt-2">
                  <LifeBuoy className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                  <span>Contact d'urgence : <strong>{client.emergency_contact_name || "—"}</strong>{client.emergency_contact_phone && <> · <a href={`tel:${client.emergency_contact_phone}`} className="underline" data-testid="client-ec-phone">{client.emergency_contact_phone}</a></>}</span>
                </div>
              )}
            </div>
          </div>
        ) : (
          <div data-testid="client-not-found" className="text-xs text-slate-500 flex items-center gap-1.5 bg-slate-900/60 border border-slate-800 rounded-lg px-3 py-2">
            <UserRound className="w-3.5 h-3.5" /> Client non enregistré dans le fichier.
          </div>
        )}

        {/* Victim video */}
        <div className="space-y-2">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1.5">
            <Video className="w-3.5 h-3.5" /> Vidéo de la victime
          </p>
          {!alert.has_video ? (
            <div data-testid="video-none" className="flex items-center gap-2 text-sm text-slate-500 bg-slate-900/80 border border-slate-800 rounded-xl p-4">
              <VideoOff className="w-4 h-4" /> Aucune vidéo transmise pour cette alerte.
            </div>
          ) : videoState === "loading" ? (
            <div data-testid="video-loading" className="flex items-center gap-2 text-sm text-slate-400 bg-slate-900/80 border border-slate-800 rounded-xl p-4">
              <Loader2 className="w-4 h-4 animate-spin" /> Chargement de la vidéo...
            </div>
          ) : videoState === "error" ? (
            <div data-testid="video-error" className="flex items-center gap-2 text-sm text-red-400 bg-red-950/30 border border-red-500/30 rounded-xl p-4">
              <VideoOff className="w-4 h-4" /> Impossible de charger la vidéo.
            </div>
          ) : videoUrl ? (
            <video
              data-testid="victim-video"
              src={videoUrl}
              controls
              playsInline
              className="w-full rounded-xl border border-slate-800 bg-black max-h-72"
            />
          ) : null}
        </div>

        {/* Assign agent */}
        <div className="space-y-2">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><UserCheck className="w-3.5 h-3.5" /> Agent assigné</p>
          {alert.assigned_to && (
            <div className="flex items-center gap-2 text-sm text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2" data-testid="current-assignee">
              <UserCheck className="w-4 h-4" /> Actuel : {alert.assigned_to}
            </div>
          )}
          {agents.length === 0 ? (
            <div className="flex items-center justify-between gap-2 text-sm text-slate-400 bg-slate-900/80 border border-slate-800 rounded-lg px-3 py-3">
              <span className="flex items-center gap-2"><Users className="w-4 h-4" /> Aucun agent enregistré</span>
              <Link to="/equipe" data-testid="go-to-team-link" className="text-emerald-400 hover:underline font-medium shrink-0">Gérer l'équipe</Link>
            </div>
          ) : (
            <div className="flex gap-2">
              <Select value={selectedAgentId} onValueChange={setSelectedAgentId}>
                <SelectTrigger data-testid="assign-agent-select" className="bg-slate-950 border-slate-700 text-slate-100 h-10">
                  <SelectValue placeholder="Choisir un agent..." />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700 text-slate-100">
                  {agents.map((a) => {
                    const busy = !!a.assignment;
                    return (
                      <SelectItem key={a.id} value={a.id} data-testid={`assign-agent-option-${a.id}`} className="focus:bg-slate-800">
                        <span className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${busy ? "bg-amber-400" : "bg-emerald-400"}`} />
                          {a.first_name} {a.last_name} · {a.zone}
                          <span className={busy ? "text-amber-400" : "text-emerald-400"}>
                            — {busy ? "en intervention" : "disponible"}
                          </span>
                        </span>
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
              <Button data-testid="assign-agent-submit-btn" onClick={doAssign} disabled={busy === "assign" || !selectedAgentId} className="bg-yellow-500 hover:bg-yellow-400 text-slate-900 font-semibold h-10 shrink-0">
                {busy === "assign" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Assigner"}
              </Button>
            </div>
          )}
          <p className="text-[11px] text-slate-500 flex items-center gap-1.5"><MapPin className="w-3 h-3 text-blue-400" /> Le client apparaît en rouge, l'agent en bleu ; un SMS est envoyé à l'agent.</p>
        </div>

        {/* Notes */}
        <div className="space-y-2">
          <p className="text-xs font-mono uppercase tracking-wider text-slate-400 flex items-center gap-1.5"><StickyNote className="w-3.5 h-3.5" /> Notes d'intervention</p>
          {(alert.notes || []).length > 0 && (
            <div className="space-y-2 mb-2">
              {alert.notes.map((n) => (
                <div key={n.note_id} className="bg-slate-900/80 border border-slate-800 rounded-lg p-3">
                  <p className="text-sm text-slate-200">{n.text}</p>
                  <p className="text-[10px] font-mono text-slate-500 mt-1">{n.author} · {timeAgo(n.ts)}</p>
                </div>
              ))}
            </div>
          )}
          <Textarea
            data-testid="intervention-notes-input"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Consigner une observation..."
            rows={2}
            className="bg-slate-950 border-slate-700 text-slate-100 resize-none"
          />
          <Button data-testid="save-notes-btn" onClick={doNote} disabled={busy === "note"} variant="outline" className="w-full border-slate-700 text-slate-200 hover:bg-slate-800 h-10">
            {busy === "note" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4 mr-2" /> Ajouter la note</>}
          </Button>
        </div>
      </div>

      <div className="p-4 border-t border-slate-800 shrink-0">
        <Button data-testid="mark-handled-btn" onClick={doHandle} disabled={busy === "handle"} className="w-full h-12 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold text-base">
          {busy === "handle" ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-2" /> Marquer comme traitée</>}
        </Button>
      </div>
    </div>
  );
};

export default AlertDetail;
